import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { ConfigProvider, Tooltip } from 'antd';
import merge from 'lodash/merge';
import { createGraphConfig } from '@antv/xflow-core';
import { Shape } from '@antv/x6';
import { NODE_HEIGHT, ASPECTRATIONODE, setNodeRender, setGroupRender, } from '../flowchart-node-panel';
import { movedNode, resizeNode, changePortsVisible, addTools, removeTools, setProps } from './utils';
/** 临时边 */
const TEMP_EGDE = 'flowchart-connecting-edge';
/** 自定义React节点 */
const ANT_PREFIX = 'ant';
export var NsAddEdgeEvent;
(function (NsAddEdgeEvent) {
    NsAddEdgeEvent.EVENT_NAME = 'ADD_FLOWCHART_EDGE_CMD_EVENT';
})(NsAddEdgeEvent || (NsAddEdgeEvent = {}));
const defaultEdgeConfig = {
    attrs: {
        line: {
            stroke: '#A2B1C3',
            targetMarker: {
                name: 'block',
                width: 12,
                height: 8,
            },
            strokeDasharray: '5 5',
            strokeWidth: 1,
        },
    },
};
const XFlowEdge = Shape.Edge.registry.register('xflow', Shape.Edge.define({
    zIndex: 1,
    highlight: true,
    name: TEMP_EGDE,
    label: '',
    anchor: {
        name: 'midSide',
        args: {
            dx: 10,
        },
    },
    attrs: defaultEdgeConfig.attrs,
    data: {
        label: '',
    },
}), true);
export const useGraphConfig = createGraphConfig((config, proxy) => {
    const { config: canvasConfig = {}, useConfig, mode = 'edit', showPortsOnNodeSelected = false, edgeConfig = {}, } = proxy.getValue();
    config.setEdgeTypeParser(edge => edge === null || edge === void 0 ? void 0 : edge.renderKey);
    /** 这里比较黑，props 共享*/
    setProps(Object.assign(Object.assign({}, proxy.getValue()), { graphConfig: config }));
    setNodeRender(config);
    setGroupRender(config);
    config.setX6Config(merge({
        grid: true,
        history: true,
        resizing: {
            enabled: true,
            minWidth: NODE_HEIGHT,
            minHeight: NODE_HEIGHT,
            preserveAspectRatio: shape => {
                const { data } = shape;
                return ASPECTRATIONODE.includes(data.name);
            },
        },
        snapline: {
            enabled: true,
        },
        connecting: {
            router: 'manhattan',
            connector: {
                name: 'rounded',
                args: {
                    radius: 8,
                },
            },
            anchor: 'center',
            connectionPoint: 'anchor',
            allowBlank: false,
            snap: {
                radius: 20,
            },
            createEdge() {
                const tempEdge = new XFlowEdge({});
                this.once('edge:connected', args => {
                    const { edge, isNew } = args;
                    /** 没有edge:connected时，会导致graph.once的事件没有执行 */
                    if (isNew && edge && edge.isEdge() && tempEdge === edge) {
                        const targetNode = edge.getTargetCell();
                        if (targetNode && targetNode.isNode()) {
                            const targetPortId = edge.getTargetPortId();
                            const sourcePortId = edge.getSourcePortId();
                            const sourceCellId = edge.getSourceCellId();
                            const targetCellId = edge.getTargetCellId();
                            const customEdgeConfig = typeof edgeConfig === 'function' ? edgeConfig(edge) : edgeConfig;
                            this.trigger(NsAddEdgeEvent.EVENT_NAME, Object.assign({ targetPortId,
                                sourcePortId, source: sourceCellId, target: targetCellId, edge: edge, tempEdgeId: tempEdge.id }, merge(defaultEdgeConfig, customEdgeConfig)));
                        }
                    }
                });
                return tempEdge;
            },
            validateEdge: args => {
                var _a;
                const { edge } = args;
                return !!((_a = edge === null || edge === void 0 ? void 0 : edge.target) === null || _a === void 0 ? void 0 : _a.port);
            },
            // 是否触发交互事件
            validateMagnet() {
                // 所有锚点均可触发
                return true;
            },
            // 显示可用的链接桩
            validateConnection({ sourceView, targetView, targetMagnet }) {
                // 不允许连接到自己
                if (sourceView === targetView) {
                    return false;
                }
                const node = targetView.cell;
                // 判断目标链接桩是否可连接
                if (targetMagnet) {
                    const portId = targetMagnet.getAttribute('port');
                    const port = node.getPort(portId);
                    return !(port && port.connected);
                }
                return;
            },
        },
        highlighting: {
            nodeAvailable: {
                name: 'className',
                args: {
                    className: 'available',
                },
            },
            magnetAvailable: {
                name: 'className',
                args: {
                    className: 'available',
                },
            },
            magnetAdsorbed: {
                name: 'className',
                args: {
                    className: 'adsorbed',
                },
            },
        },
        onPortRendered(args) {
            const { port } = args;
            const { contentSelectors } = args;
            const container = contentSelectors && contentSelectors.content;
            const placement = port.group;
            const clz = classnames('xflow-port', { connected: port.connected });
            if (container) {
                ReactDOM.render((React.createElement(ConfigProvider, { prefixCls: ANT_PREFIX },
                    React.createElement(Tooltip, { title: port.tooltip, placement: placement },
                        React.createElement("span", { className: clz })))), container);
            }
        },
    }, canvasConfig));
    /** 内交互，上层通过实例绑定 */
    config.setEvents([
        {
            eventName: 'node:selected',
            callback: () => {
                mode === 'edit' && changePortsVisible(false);
            },
        },
        {
            eventName: 'edge:dblclick',
            callback: e => {
                addTools(e);
            },
        },
        {
            eventName: 'edge:mouseleave',
            callback: (e, cmds) => {
                removeTools(e, cmds);
            },
        },
        {
            eventName: 'node:mouseenter',
            callback: e => {
                mode === 'edit' && changePortsVisible(true, e, showPortsOnNodeSelected);
            },
        },
        {
            eventName: 'node:mouseleave',
            callback: e => {
                changePortsVisible(false, e);
            },
        },
        {
            eventName: 'node:moved',
            callback: (e, cmds) => {
                movedNode(e, cmds);
            },
        },
        {
            eventName: 'node:resized',
            callback: (e, cmds) => {
                resizeNode(e, cmds);
            },
        },
    ]);
    if (typeof useConfig === 'function') {
        useConfig(config);
    }
});
//# sourceMappingURL=config-graph.js.map